// Implements: F-MCK-017, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * The trash: restore, and delete for good after a confirmation.
 *
 * The permanent delete asks first with `ConfirmDialog` (never `confirm()`),
 * about a specific row held in state: a dialog that confirms "the last button
 * pressed" deletes the wrong mockup the day two rows are on screen. Without
 * `permanent=true` the backend only trashes, so the flag is the whole
 * difference between this action and the move to the trash of the gallery.
 */

import { useCallback, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TrashEntry } from "../../../types/api";
import { Button } from "../../../shared/ui/atoms/Button";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { TrashTable } from "../components/TrashTable";
import { useMockupActions } from "../hooks/useMockupActions";
import { useTrash } from "../hooks/useTrash";
import styles from "./TrashContainer.module.css";

export function TrashContainer(): ReactElement {
  const { t } = useTranslation();
  const trash = useTrash();
  const { restore, deleteForever } = useMockupActions();
  const { push } = useToast();
  const [asking, setAsking] = useState<TrashEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (action: () => Promise<boolean>, successMessage: string): Promise<void> => {
      setBusy(true);
      const done = await action();
      setBusy(false);
      push(
        done
          ? { tone: "success", message: successMessage }
          : { tone: "danger", message: t("common.state.error") },
      );
    },
    [push, t],
  );

  const confirm = useCallback(async (): Promise<void> => {
    if (asking === null) return;
    const target = asking;
    setAsking(null);
    await run(() => deleteForever(target.id), t("gallery.trash.deleted"));
  }, [asking, run, deleteForever, t]);

  const entries = trash.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className={styles.trash}>
      {trash.isPending ? <TableSkeleton /> : null}

      {trash.isError ? (
        <ErrorState
          error={trash.error}
          onRetry={() => {
            void (trash.isFetchNextPageError ? trash.fetchNextPage() : trash.refetch());
          }}
        />
      ) : null}

      {trash.data !== undefined && !trash.isError && entries.length === 0 ? (
        <EmptyState icon="trash" title={t("gallery.trash.empty")} body={t("gallery.trash.emptyBody")} />
      ) : null}

      {entries.length > 0 ? (
        <TrashTable
          entries={entries}
          disabled={busy}
          onRestore={(row) => {
            void run(() => restore(row.id), t("gallery.trash.restored"));
          }}
          onDeleteForever={setAsking}
        />
      ) : null}

      {trash.hasNextPage ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            loading={trash.isFetchingNextPage}
            onClick={() => {
              void trash.fetchNextPage();
            }}
          >
            {t("gallery.loadMore")}
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={asking !== null}
        title={t("gallery.trash.confirmTitle")}
        body={t("gallery.trash.confirmBody")}
        confirmLabel={t("gallery.trash.deleteForever")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={() => {
          void confirm();
        }}
        onCancel={() => {
          setAsking(null);
        }}
      />
    </div>
  );
}
